import { EditorStarterItem } from '../item-type';
import {
  TransitionType,
  TransitionDirection,
} from '../../editor-property-panel/ui/contents/transition/transition-drag-data';

// 트랜지션 아이템 타입 (일반 아이템과 다르게 캔버스에 렌더링되지 않음)
export interface TransitionItem {
  id: string;
  type: 'transition';
  // 트랜지션 효과 타입 (fade, slide, wipe 등)
  transitionType: TransitionType;
  // 트랜지션 방향 (slide, wipe, flip에서만 사용)
  direction?: TransitionDirection;
  // 트랜지션 duration (프레임)
  durationInFrames: number;
  // 연결된 두 클립의 ID
  sourceItemId: string; // 앞쪽 클립 (outgoing)
  targetItemId: string; // 뒤쪽 클립 (incoming)
  // 트랙 ID (트랜지션이 적용되는 트랙)
  trackId: string;
  // 트랜지션 시작 프레임. capsuleCover는 sourceItem 끝 - 전체 duration,
  // 일반 트랜지션은 sourceItem 끝 - durationInFrames/2.
  from: number;
  // frozen frame 정보 (아이템 길이가 부족할 때 마지막 프레임을 고정)
  sourceFrozenFrames?: number; // sourceItem(앞쪽 클립) 끝에서 frozen 적용된 프레임 수
  targetFrozenFrames?: number; // targetItem(뒤쪽 클립) 시작에서 frozen 적용된 프레임 수
  // 논리적 확장 정보 (UI에는 반영되지 않고 재생 시에만 사용)
  sourceExtendedFrames?: number; // sourceItem이 뒤로 확장된 프레임 수
  targetExtendedFrames?: number; // targetItem이 앞으로 확장된 프레임 수
  targetExtendedAssetStartDelta?: number; // targetItem의 assetStart 조정값 (초 단위, 음수)
  // capsuleCover 전용 영상 합성 색상. 없는 기존 payload 는 기본 색상으로 렌더한다.
  primaryColor?: string;
  secondaryColor?: string;
}

export const isTransitionItem = (
  item: EditorStarterItem | TransitionItem | undefined,
): item is TransitionItem => {
  if (!item) return false;
  return item.type === 'transition';
};

export const getMaxTransitionDurationInFrames = (
  transition: TransitionItem,
  sourceItem: EditorStarterItem,
  targetItem: EditorStarterItem,
) =>
  transition.transitionType === 'capsuleCover'
    ? sourceItem.durationInFrames
    : Math.min(sourceItem.durationInFrames, targetItem.durationInFrames);

// 트랜지션 기본 duration (프레임) - 30fps 기준 1초
export const DEFAULT_TRANSITION_DURATION_IN_FRAMES = 30;

// 트랜지션 생성 헬퍼 함수
export const createTransitionItem = ({
  id,
  transitionType,
  direction,
  durationInFrames = DEFAULT_TRANSITION_DURATION_IN_FRAMES,
  sourceItemId,
  targetItemId,
  trackId,
  from,
  sourceFrozenFrames = 0,
  targetFrozenFrames = 0,
  sourceExtendedFrames = 0,
  targetExtendedFrames = 0,
  targetExtendedAssetStartDelta = 0,
  primaryColor,
  secondaryColor,
}: {
  id: string;
  transitionType: TransitionType;
  direction?: TransitionDirection;
  durationInFrames?: number;
  sourceItemId: string;
  targetItemId: string;
  trackId: string;
  from: number;
  sourceFrozenFrames?: number;
  targetFrozenFrames?: number;
  sourceExtendedFrames?: number;
  targetExtendedFrames?: number;
  targetExtendedAssetStartDelta?: number;
  primaryColor?: string;
  secondaryColor?: string;
}): TransitionItem => ({
  id,
  type: 'transition',
  transitionType,
  direction,
  durationInFrames,
  sourceItemId,
  targetItemId,
  trackId,
  from,
  sourceFrozenFrames,
  targetFrozenFrames,
  sourceExtendedFrames,
  targetExtendedFrames,
  targetExtendedAssetStartDelta,
  primaryColor,
  secondaryColor,
});

// 최소 트랜지션 duration (프레임) - 0.5초 @ 30fps
export const MIN_TRANSITION_DURATION_IN_FRAMES = 15;
